import { ThemeProvider } from "@mui/material";
import { notEmpty, theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "~/components/app-bar/hooks";
import type { ArticleRenderChildProps } from "~/components/article/article-render";
import {
  AuthorDetail,
  CommentAndReaction,
  Content,
  Correction,
  DesktopHeaderAdSlot,
  Leading,
  Left,
  MobileContentFooterAdSlot,
  MobileContentHeaderAdSlot,
  MobileFooterAdSlot,
  MoreOnThis,
  StyledArticleContentRightSection,
  TabletContentFooterAdSlot,
  Topic,
  TopPicksAdSlot,
} from "~/components/article/article-render/generic-article/styles";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import {
  useMobileInlineAdSlotIsDisabledFlag,
  useReduceWhitespaceByAvailableSpace,
} from "~/components/article/article-render/hooks";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { useContentHelper } from "~/components/content/hooks";
import type { opinionArticleContent$key } from "~/queries/__generated__/opinionArticleContent.graphql";

import {
  OpinionArticleContainer,
  RightAvailableSpaceTracker,
  StyledOpinionHeader,
  WidgetsBottom,
  WidgetsBottomAvailableSpaceTracker,
} from "./styles";
import { theme as OpinionArticleTheme } from "./theme";

export type Props = {
  reference: opinionArticleContent$key;
} & ArticleRenderChildProps;

export const OpinionArticle: FunctionComponent<Props> = ({
  isInlinePaywallDomChanged,
  isShowInlinePaywall,
  provider,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment opinionArticleContent on Content {
        entityId
        ...globalCheckerContent
        ...headerOpinionArticle
        ...hooksContentHelperContent
        ...contentSchemaRenderContent
        ...reduceWhitespaceByAvailableSpaceArticle
      }
    `,
    reference,
  );

  const { isAuthorSCMPColumnist, isEditorialOpinionType } = useContentHelper(content);
  const isColumnistArticle = isAuthorSCMPColumnist ?? false;
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    isEnabled: notEmpty(isShowInlinePaywall ?? isInlinePaywallDomChanged),
    reference: content,
    specs: {
      adSlotSize: 600,
      nativeAdSlotSize: 260,
      whitespaceAllowance: 100,
    },
    trackers: {
      right: RightAvailableSpaceTracker,
      widgetsBottom: WidgetsBottomAvailableSpaceTracker,
    },
  });

  const renderLeading = () => (
    <Leading>
      {isColumnistArticle
        ? null
        : provider.leading({
            responsiveDescriptionVariants: {
              desktopUp: "show",
              mobileUp: "hidden",
              tabletUp: "show",
            },
            responsiveImageVariants: {
              desktopUp: "normal",
              mobileUp: "square",
              tabletUp: "normal",
            },
          })}
    </Leading>
  );
  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();

  return (
    <ThemeProvider theme={OpinionArticleTheme}>
      <OpinionArticleContainer $isColumnistArticle={isColumnistArticle}>
        <DesktopHeaderAdSlot>
          {provider.adslot({
            adUnit: "d_banner1",
            autoRefreshOptions: {
              scrollBackRefreshOptions: {
                slotArticleEntityId: content.entityId,
              },
            },
            breakpoint: theme.breakpoints.up("desktop"),
            sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
          })}
        </DesktopHeaderAdSlot>

        <MobileContentHeaderAdSlot>
          {provider.adslot({
            adUnit: "m_banner1",
            autoRefreshOptions: {
              scrollBackRefreshOptions: {
                slotArticleEntityId: content.entityId,
              },
            },
            breakpoint: theme.breakpoints.down("tablet"),
            sizes: [[300, 250], "fluid"],
            withBackground: true,
            withLabel: true,
          })}
        </MobileContentHeaderAdSlot>
        <WidgetsBottom>
          {provider.leftHandSideWidgets({
            topPicksWidgetProps: {
              maximumHeight: reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.maximumHeight,
              slots: {
                footer: {
                  estimatedHeight: 260,
                  node: (
                    <TopPicksAdSlot>
                      {provider.adslot({
                        adUnit: "d_native3b",
                        breakpoint: theme.breakpoints.up("desktop"),
                        sizes: ["fluid"],
                      })}
                    </TopPicksAdSlot>
                  ),
                },
                header: {
                  estimatedHeight: 260,
                  node: (
                    <TopPicksAdSlot>
                      {provider.adslot({
                        adUnit: "d_native2b",
                        breakpoint: theme.breakpoints.up("desktop"),
                        fluidDefaultSize: {
                          height: 260,
                        },
                        sizes: ["fluid"],
                      })}
                    </TopPicksAdSlot>
                  ),
                },
              },
            },
          })}
        </WidgetsBottom>
        {reduceWhitespaceByAvailableSpaceSpec.widgetsBottom.trackerNode}
        {renderLeading()}
        <StyledOpinionHeader
          isColumnistArticle={isColumnistArticle}
          isEditorialOpinionType={isEditorialOpinionType}
          provider={provider}
          reference={content}
        />
        <Correction>{provider.correction()}</Correction>
        <Content>
          {provider.body({
            inlineAdSlots: {
              "0": {
                adUnit: "outstream1",
              },
              "1": {
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "2": {
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "3": {
                adUnit: "m_lrec4d",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "4": {
                adUnit: "m_lrec4e",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "5": {
                adUnit: "m_lrec4f",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "6": {
                adUnit: "native5",
                isDisabled: isInlineAdSlotDisabled,
              },
            },
          })}
        </Content>
        <CommentAndReaction>{provider.commentTriggerAndReaction()}</CommentAndReaction>
        <MoreOnThis>{provider.moreOnThis({ variant: "horizontal" })}</MoreOnThis>
        <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
        <Topic>{provider.topics()}</Topic>
        <Left $stickyStackedHeight={appBarStickyHeight}>
          {provider.shareWidget({
            variant: "generic",
          })}
          {provider.actionWidget()}
        </Left>
        <StyledArticleContentRightSection
          entityId={content.entityId}
          {...reduceWhitespaceByAvailableSpaceSpec.right}
          provider={provider}
        />
        {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}
        <ClientSideSuspense>
          <GlobalChecker reference={content}>
            <TabletContentFooterAdSlot>
              {provider.adslot({
                adUnit: "native4",
                breakpoint: theme.breakpoints.only("tablet"),
                sizes: ["fluid"],
              })}
            </TabletContentFooterAdSlot>
            <MobileContentFooterAdSlot>
              {provider.adslot({
                adUnit: "native4",
                breakpoint: theme.breakpoints.down("tablet"),
                sizes: ["fluid"],
              })}
            </MobileContentFooterAdSlot>
            <MobileFooterAdSlot>
              {provider.adslot({
                adUnit: "m_lrec3a",
                breakpoint: theme.breakpoints.down("tablet"),
                sizes: [[300, 250], "fluid"],
                withBackground: true,
                withLabel: true,
              })}
            </MobileFooterAdSlot>
          </GlobalChecker>
        </ClientSideSuspense>
      </OpinionArticleContainer>
    </ThemeProvider>
  );
};

OpinionArticle.displayName = "OpinionArticle";
